import { fail, pass, rangeStatus, warn, type SeoRule } from '../rule';
import { DEFAULT_WORD_COUNT_RANGE, FALLBACK_WORD_COUNT_RANGE, T, THIN_CONTENT_WORDS } from '../thresholds';
import { normalizeFr } from '../tokenize-fr';
import { SeoRuleKey, SeoScoreGroup } from '../types';
import { FR_STOPWORDS } from '../wordlists-fr';

const G = SeoScoreGroup.LENGTH_META;

export function targetWordRange(ctx: {
  options: { target_word_count?: number; content_type?: string };
  brief?: { target_word_count: number; content_type: string };
}): [number, number] {
  const target = ctx.options.target_word_count ?? ctx.brief?.target_word_count;
  if (target !== undefined) {
    return [
      Math.round(target * (1 - T.WORD_COUNT_TOLERANCE.PASS)),
      Math.round(target * (1 + T.WORD_COUNT_TOLERANCE.PASS)),
    ];
  }
  const type = ctx.options.content_type ?? ctx.brief?.content_type;
  const range = type ? DEFAULT_WORD_COUNT_RANGE[type as keyof typeof DEFAULT_WORD_COUNT_RANGE] : undefined;
  return range ?? FALLBACK_WORD_COUNT_RANGE;
}

export const LENGTH_META_RULES: SeoRule[] = [
  {
    key: SeoRuleKey.WORD_COUNT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const words = ctx.wordCount;
      const [lo, hi] = targetWordRange(ctx);
      const threshold = `${lo}–${hi} mots`;
      if (words < THIN_CONTENT_WORDS) return fail(words, threshold, undefined, true);
      if (words >= lo && words <= hi) return pass(words, threshold);
      const warnLo = Math.round(lo * (1 - (T.WORD_COUNT_TOLERANCE.WARN - T.WORD_COUNT_TOLERANCE.PASS)));
      const warnHi = Math.round(hi * (1 + (T.WORD_COUNT_TOLERANCE.WARN - T.WORD_COUNT_TOLERANCE.PASS)));
      if (words >= warnLo && words <= warnHi) return warn(words, threshold);
      return fail(words, threshold);
    },
  },
  {
    key: SeoRuleKey.META_TITLE_LENGTH,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const meta = ctx.input.meta_title?.trim();
      if (!meta) return fail('absent', `${T.META_TITLE.PASS[0]}–${T.META_TITLE.PASS[1]} caractères`);
      return rangeStatus(meta.length, T.META_TITLE.PASS, T.META_TITLE.WARN, (v): string => `${v} caractères`);
    },
  },
  {
    key: SeoRuleKey.META_DESC_LENGTH,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const meta = ctx.input.meta_description?.trim();
      if (!meta) return fail('absente', `${T.META_DESC.PASS[0]}–${T.META_DESC.PASS[1]} caractères`);
      return rangeStatus(meta.length, T.META_DESC.PASS, T.META_DESC.WARN, (v): string => `${v} caractères`);
    },
  },
  {
    key: SeoRuleKey.META_TITLE_UNIQUE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const meta = ctx.input.meta_title?.trim();
      if (!meta || ctx.title.length === 0) return null;
      return normalizeFr(meta) === normalizeFr(ctx.title)
        ? warn('identique au H1', 'différent')
        : pass('différent du H1');
    },
  },
  {
    key: SeoRuleKey.SLUG_QUALITY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const slug = ctx.input.slug?.trim();
      if (!slug) return null;
      const issues: string[] = [];
      const parts = slug.split(/[-_]+/).filter((p): boolean => p.length > 0);
      // Les mots de la requête ne comptent ni comme mots vides ni dans le plafond (2026.09.5) :
      // KW_IN_SLUG exige la requête, « rendez-vous-…-de-beaute » ne peut pas perdre « vous » ni « de ».
      const kwParts = new Set((ctx.matcher?.normalized ?? '').split(/[^a-z0-9]+/).filter((p): boolean => p.length > 0));
      const maxWords = Math.max(T.SLUG_MAX_WORDS, kwParts.size);
      if (parts.length > maxWords) issues.push(`${parts.length} mots (max ${maxWords})`);
      if (slug.length > T.SLUG_MAX_CHARS) issues.push(`${slug.length} caractères (max ${T.SLUG_MAX_CHARS})`);
      if (/[^a-z0-9-]/.test(slug)) issues.push('caractères non autorisés (accent, majuscule, underscore)');
      const stop = parts.filter((p): boolean => FR_STOPWORDS.has(p) && !kwParts.has(p));
      if (stop.length > 0) issues.push(`mots vides : ${stop.join(', ')}`);
      if (/(^|-)(19|20)\d{2}(-|$)/.test(slug)) issues.push('année dans le slug (périmera)');
      if (issues.length === 0) return pass('propre');
      if (issues.length === 1) return warn(1, '0 écart', issues);
      return fail(issues.length, '0 écart', issues);
    },
  },
  {
    key: SeoRuleKey.EXCERPT_PRESENT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const excerpt = ctx.input.excerpt?.trim();
      if (excerpt === undefined) return null;
      if (excerpt.length === 0) return fail('absent', `${T.EXCERPT.PASS[0]}–${T.EXCERPT.PASS[1]} caractères`);
      return rangeStatus(excerpt.length, T.EXCERPT.PASS, [1, 10_000], (v): string => `${v} caractères`);
    },
  },
];
